// ─── Feuille : ÉDITION d'un compte ────────────────────────────────
// Le COMMERCE change forfait, statut et options ; les RÔLES sont réservés à
// l'admin. Les actions du cycle de vie (approuver, rejeter, mot de passe,
// supprimer/restaurer) sont regroupées en bas. Les règles Firestore tranchent.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import {
  Feuille, ChoixPuces, Bascule, stylesForm, useUI, useT, useAuth,
  aDemandeForfait, aDemandeAchats, poidsSelection, prixDePoints, ecouterBareme,
  confirmerSuppression, confirmerChoix, ecouterPlans, nomPlan, poidsDe,
} from '@mop/core';
import {
  majCompte, definirRoles, appliquerDemandeForfait, refuserDemandeForfait,
  accepterDemandeAchats, refuserDemandeAchats, ecouterConfigForfaits, PLANS, STATUTS,
} from './services/admin';
import {
  supprimerCompte, supprimerCompteDefinitif, restaurerCompte, approuverCompte, rejeterCompte,
  envoyerLienMotDePasse, definirSansPub, definirAccesIllimite, definirGroupeIVV,
} from './services/comptes';
import { CATALOGUE_SUITE } from './catalogueSuite';
import FeuilleGroupeIVV from './FeuilleGroupeIVV';

// Date + heure lisibles depuis un horodatage ISO (creeLe / derniereConnexionLe).
const fmtDateHeure = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

// Quotas : pas dans le catalogue de fonctions, leurs libellés vivent dans les
// clés i18n déjà posées pour l'écran Forfaits (admin.jetonsGestion…).
const LABEL_QUOTA = {
  gestion: 'admin.jetonsGestion', vue: 'admin.jetonsVue', espaces: 'admin.maxEspaces',
  calendriers: 'admin.typeCalendriers', inventaires: 'admin.typeInventaires',
  repas: 'admin.typeRepas', comptes: 'admin.typeComptes',
};

// `ouvrirEnEdition` (23/08, Cédric) : la fiche s'ouvre par défaut en LECTURE
// SEULE (tap sur la carte) — glisser à droite (LigneSwipe) ouvre directement
// en édition. Le geste ✏️ dans les deux cas bascule vers l'autre mode.
export default function FeuilleCompte({ compte, ouvrirEnEdition = false, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { estAdmin, user } = useAuth();
  const [lecture, setLecture] = useState(true);
  const [prenom, setPrenom] = useState('');
  const [plan, setPlan] = useState('gratuit');
  const [statut, setStatut] = useState('actif');
  const [admin, setAdmin] = useState(false);
  const [commerce, setCommerce] = useState(false);
  const [ivv, setIvv] = useState(false);
  const [sansPub, setSansPub] = useState(false);
  const [illimite, setIllimite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  // Noms réels des forfaits (config/plans) — les identifiants bruts
  // (`gratuit`, `famille`…) ne sont pas ce qu'on montre à l'écran.
  const [plansCfg, setPlansCfg] = useState({});
  useEffect(() => ecouterPlans(setPlansCfg), []);
  // Poids des fonctions (config/fonctionnalites.poids) + barème (config/pointsALaCarte)
  // — nécessaires pour chiffrer une demande d'achat à la carte avant de l'accepter.
  const [cfgFonctions, setCfgFonctions] = useState({});
  useEffect(() => ecouterConfigForfaits(setCfgFonctions, () => {}), []);
  const [bareme, setBareme] = useState([]);
  useEffect(() => ecouterBareme(setBareme, () => {}), []);
  const [groupe, setGroupe] = useState(false);

  useEffect(() => {
    if (!compte) return;
    setPrenom(compte.prenom || '');
    setPlan(compte.plan || 'gratuit');
    setStatut(compte.statut || 'actif');
    setAdmin(!!compte.admin || compte.role === 'admin');
    setCommerce(!!compte.commerce || compte.role === 'commerce');
    setIvv(!!compte.ivv);
    setSansPub(!!compte.noPub);
    setIllimite(!!compte.accesIllimite);
    setBusy(false); setErr(''); setInfo('');
    setLecture(!ouvrirEnEdition);
  }, [compte, ouvrirEnEdition]);

  const initRoles = (c) => ({
    admin: !!c?.admin || c?.role === 'admin',
    commerce: !!c?.commerce || c?.role === 'commerce',
    ivv: !!c?.ivv,
  });
  const r0 = initRoles(compte);
  const rolesChanges = !!compte && (admin !== r0.admin || commerce !== r0.commerce || ivv !== r0.ivv);
  const modifie = !!compte && (prenom.trim() !== (compte.prenom || '') || plan !== (compte.plan || 'gratuit')
    || statut !== (compte.statut || 'actif') || sansPub !== !!compte.noPub || illimite !== !!compte.accesIllimite || rolesChanges);

  async function enregistrer() {
    if (!compte || !modifie || busy) return;
    setBusy(true); setErr('');
    try {
      await majCompte(compte.uid, { prenom: prenom.trim(), plan, statut });
      if (sansPub !== !!compte.noPub) await definirSansPub(compte.uid, sansPub);
      if (illimite !== !!compte.accesIllimite) await definirAccesIllimite(compte.uid, illimite);
      // RÔLES : écriture réelle PUIS pose des custom claims sur le jeton.
      if (estAdmin && rolesChanges) {
        await definirRoles(compte.uid, compte.email, { admin, commerce, ivv }, user?.email);
      }
      onFermer?.();
    } catch (e) { setErr(e?.message || String(e)); }
    setBusy(false);
  }

  // Actions immédiates (hors disquette) — chacune confirmée.
  const agir = async (fn, message) => {
    setBusy(true); setErr(''); setInfo('');
    try { await fn(); if (message) setInfo(message); } catch (e) { setErr(e?.message || String(e)); }
    setBusy(false);
  };
  const demande = aDemandeForfait(compte) ? compte.planDemande : null;
  const supprime = compte?.statut === 'supprime';

  // Demande d'achats à la carte : résout les libellés (catalogue + quotas) et
  // le prix qui en résulterait, pour que la décision se prenne les yeux ouverts.
  const demandeAchats = aDemandeAchats(compte) ? compte.achatsDemande : null;
  const lignesDemande = demandeAchats ? [
    ...(demandeAchats.fonctions || []).map(cle => {
      const def = CATALOGUE_SUITE.find(d => d.key === cle);
      return { label: def?.label || cle, poids: poidsDe(cfgFonctions, def || { key: cle }) };
    }),
    ...Object.entries(demandeAchats.quotas || {}).map(([cle, n]) => ({
      label: `+${n} ${t(LABEL_QUOTA[cle] || cle)}`, poids: poidsDe(cfgFonctions, { key: cle }) * n,
    })),
  ] : [];
  // Le prix se juge sur le TOTAL (achats déjà en place + cette demande), pas
  // sur la demande isolée : un compte qui a déjà des achats peut basculer de
  // palier de barème avec un tout petit ajout.
  const poidsDune = (achatsFn, achatsQuo) => poidsSelection({
    fonctions: Object.keys(achatsFn || {}).map(cle => ({ poids: poidsDe(cfgFonctions, CATALOGUE_SUITE.find(d => d.key === cle) || { key: cle }) })),
    quotas: Object.fromEntries(Object.entries(achatsQuo || {}).map(([cle, n]) => [cle, { n, poids: poidsDe(cfgFonctions, { key: cle }) }])),
  });
  const pointsExistants = poidsDune(compte?.fonctionnalitesAchetees, compte?.quotasAchetes);
  const pointsDemande = demandeAchats ? poidsDune(
    Object.fromEntries((demandeAchats.fonctions || []).map(cle => [cle, true])),
    demandeAchats.quotas,
  ) : 0;
  const pointsInclusPlan = cfgFonctions.plans?.[compte?.plan]?.pointsInclus ?? 0;
  const prixAvant = prixDePoints(bareme, Math.max(0, pointsExistants - pointsInclusPlan));
  const prixApres = prixDePoints(bareme, Math.max(0, pointsExistants + pointsDemande - pointsInclusPlan));

  return (
    <>
    <Feuille visible={!!compte} titre={compte?.email || t('admin.compte')} emoji="👤" hauteur={0.86}
      onFermer={onFermer}
      action={lecture
        ? { icone: '✏️', label: t('commun.editer'), onPress: () => setLecture(false) }
        : { icone: '💾', label: t('commun.enregistrer'), disabled: !modifie || busy, onPress: enregistrer }}>
      {!!err && <Text style={s.err}>⚠️ {err}</Text>}
      {!!info && <Text style={s.info}>✓ {info}</Text>}

      {/* Informations : toujours affichées, lecture ou édition (23/08). */}
      <View style={s.infos}>
        <Text style={s.infosLigne}>📅 {t('admin.creeLe')} : {fmtDateHeure(compte?.creeLe)}</Text>
        <Text style={s.infosLigne}>🕓 {t('admin.derniereConnexion')} : {fmtDateHeure(compte?.derniereConnexionLe)}</Text>
      </View>

      {/* Demande de forfait déposée par l'utilisateur. */}
      {!!demande && (
        <View style={s.demande}>
          <Text style={s.demandeTxt}>⏳ {t('admin.demandeForfait', { plan: demande })}</Text>
          <View style={s.demandeBtns}>
            <TouchableOpacity onPress={() => agir(() => refuserDemandeForfait(compte.uid))} disabled={busy}>
              <Text style={s.refuser}>{t('admin.refuser')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.accepter} disabled={busy}
              onPress={() => agir(() => appliquerDemandeForfait(compte.uid, demande), t('admin.forfaitApplique'))}>
              <Text style={s.accepterTxt}>{t('admin.accepter')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Demande d'achats à la carte : fonctions + plafonds, avec le prix. */}
      {!!demandeAchats && (
        <View style={s.demande}>
          <Text style={s.demandeTxt}>🛒 {t('admin.demandeAchats')}</Text>
          {lignesDemande.map((l, i) => (
            <Text key={i} style={s.demandeLigne}>· {l.label} ({l.poids} pt{l.poids > 1 ? 's' : ''})</Text>
          ))}
          <Text style={s.demandeLigne}>
            {t('admin.demandeAchatsResume', { n: pointsDemande })} · {prixAvant.toFixed(2).replace('.', ',')} € → {prixApres.toFixed(2).replace('.', ',')} €/mois
          </Text>
          <View style={s.demandeBtns}>
            <TouchableOpacity onPress={() => agir(() => refuserDemandeAchats(compte.uid))} disabled={busy}>
              <Text style={s.refuser}>{t('admin.refuser')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.accepter} disabled={busy}
              onPress={() => agir(() => accepterDemandeAchats(compte), t('admin.achatsAppliques'))}>
              <Text style={s.accepterTxt}>{t('admin.accepter')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Inscription en attente : approuver / rejeter. */}
      {compte?.statut === 'en_attente' && (
        <View style={s.demande}>
          <Text style={s.demandeTxt}>🕓 {t('admin.inscriptionEnAttente')}</Text>
          <View style={s.demandeBtns}>
            <TouchableOpacity onPress={() => agir(() => rejeterCompte(compte.uid), t('admin.compteRejete'))} disabled={busy}>
              <Text style={s.refuser}>{t('admin.rejeter')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={s.accepter} disabled={busy}
              onPress={() => agir(() => approuverCompte(compte.uid), t('admin.compteApprouve'))}>
              <Text style={s.accepterTxt}>{t('admin.approuver')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      <Text style={s.lbl}>{t('admin.prenom')}</Text>
      {lecture ? <Text style={s.valeurTxt}>{prenom || '—'}</Text> : (
        <TextInput style={s.input} value={prenom} onChangeText={setPrenom} autoCapitalize="words"
          placeholder={t('admin.phPrenom')} placeholderTextColor={UI.faint} />
      )}

      <Text style={s.lbl}>{t('admin.forfait')}</Text>
      {lecture ? <Text style={s.valeurTxt}>{nomPlan(plansCfg, plan)}</Text> : (
        <ChoixPuces value={plan} onChange={setPlan} options={PLANS.map(p => ({ id: p, label: nomPlan(plansCfg, p) }))} />
      )}

      <Text style={s.lbl}>{t('admin.statut')}</Text>
      {lecture ? <Text style={s.valeurTxt}>{t(`admin.statut_${statut}`)}</Text> : (
        <ChoixPuces value={statut} onChange={setStatut}
          options={STATUTS.map(x => ({ id: x, label: t(`admin.statut_${x}`) }))} />
      )}

      <Text style={s.lbl}>{t('admin.options')}</Text>
      <View style={s.ligne}>
        <Text style={{ fontSize: 16 }}>🚫</Text>
        <Text style={s.ligneTxt}>{t('admin.sansPub')}</Text>
        {lecture ? <Text style={s.valeur}>{t(sansPub ? 'commun.oui' : 'commun.non')}</Text>
          : <Bascule valeur={sansPub} onChange={setSansPub} accessibilityLabel={t('admin.sansPub')} />}
      </View>
      {estAdmin && (
        <View style={s.ligne}>
          <Text style={{ fontSize: 16 }}>♾️</Text>
          <Text style={s.ligneTxt}>{t('admin.accesIllimite')}</Text>
          {lecture ? <Text style={s.valeur}>{t(illimite ? 'commun.oui' : 'commun.non')}</Text>
            : <Bascule valeur={illimite} onChange={setIllimite} accessibilityLabel={t('admin.accesIllimite')} />}
        </View>
      )}

      {/* Rôles : ADMIN uniquement (le commerce ne s'auto-promeut pas). */}
      {estAdmin && (
        <>
          <Text style={s.lbl}>{t('admin.roles')}</Text>
          {[['admin', '🛡️', admin, setAdmin], ['commerce', '💼', commerce, setCommerce], ['ivv', '🐞', ivv, setIvv]].map(([cle, ic, val, set]) => (
            <View key={cle} style={s.ligne}>
              <Text style={{ fontSize: 16 }}>{ic}</Text>
              <Text style={s.ligneTxt}>{t(`admin.role_${cle}`)}</Text>
              {lecture ? <Text style={s.valeur}>{t(val ? 'commun.oui' : 'commun.non')}</Text>
                : <Bascule valeur={val} onChange={set} accessibilityLabel={t(`admin.role_${cle}`)} />}
            </View>
          ))}
          {/* Identité de valideur PARTAGÉE entre plusieurs comptes — pas
              touchable en lecture seule (pas d'ouverture d'édition annexe). */}
          {ivv && (
            <TouchableOpacity style={s.ligne} disabled={lecture} onPress={() => setGroupe(true)}>
              <Text style={{ fontSize: 16 }}>👥</Text>
              <Text style={s.ligneTxt}>{t('admin.groupeIVV')}</Text>
              <Text style={s.valeur}>{compte?.groupeIVV || t('admin.aucunGroupe')}</Text>
            </TouchableOpacity>
          )}
          <Text style={s.aide}>{t('admin.rolesAide')}</Text>
        </>
      )}

      {/* Cycle de vie du compte. */}
      <Text style={s.lbl}>{t('admin.actions')}</Text>
      <TouchableOpacity style={s.action} disabled={busy}
        onPress={() => confirmerChoix(t('admin.envoyerLienMdp'), t('admin.envoyerLienMdpMsg', { email: compte?.email }), [
          { text: t('admin.envoyer'), onPress: () => agir(() => envoyerLienMotDePasse(compte.email), t('admin.lienEnvoye')) },
          { text: t('commun.annuler'), style: 'cancel' },
        ])}>
        <Text style={s.actionTxt}>🔑 {t('admin.envoyerLienMdp')}</Text>
      </TouchableOpacity>
      {estAdmin && (supprime ? (
        <>
          <TouchableOpacity style={s.action} disabled={busy}
            onPress={() => agir(() => restaurerCompte(compte.uid), t('admin.compteRestaure'))}>
            <Text style={s.actionTxt}>♻️ {t('admin.restaurer')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[s.action, s.actionDanger]} disabled={busy}
            onPress={() => confirmerSuppression(t('admin.supprimerDefinitif'), t('admin.supprimerDefinitifMsg', { email: compte?.email }),
              () => agir(() => supprimerCompteDefinitif(compte.uid), t('admin.compteSupprimeDefinitivement')))}>
            <Text style={[s.actionTxt, { color: UI.danger }]}>🗑️ {t('admin.supprimerDefinitif')}</Text>
          </TouchableOpacity>
        </>
      ) : (
        <TouchableOpacity style={[s.action, s.actionDanger]} disabled={busy}
          onPress={() => confirmerSuppression(t('admin.supprimerCompte'), t('admin.supprimerCompteMsg', { email: compte?.email }),
            () => agir(() => supprimerCompte(compte.uid), t('admin.compteSupprime')))}>
          <Text style={[s.actionTxt, { color: UI.danger }]}>🗑️ {t('admin.supprimerCompte')}</Text>
        </TouchableOpacity>
      ))}
      <Text style={s.aide}>{t('admin.suppressionAide')}</Text>
    </Feuille>

    {/* Nom du groupe IVV — modale sœur. */}
    <FeuilleGroupeIVV visible={groupe} compte={compte}
      onEnregistrer={(nom) => definirGroupeIVV(compte.uid, nom).catch(() => {})}
      onFermer={() => setGroupe(false)} />
    </>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9,
           borderTopWidth: 0.5, borderTopColor: UI.border },
  ligneTxt: { flex: 1, fontSize: 14, color: UI.ink, fontWeight: '600' },
  valeur: { fontSize: 13, color: UI.accent, fontWeight: '700' },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 10, lineHeight: 16 },
  err: { fontSize: 12.5, color: UI.danger, fontWeight: '700', marginBottom: 6 },
  infos: { marginBottom: 8 },
  infosLigne: { fontSize: 11.5, color: UI.faint },
  // Valeur d'un champ en LECTURE SEULE — même emplacement que l'input/puces
  // qu'il remplace, mais non interactif.
  valeurTxt: { fontSize: 14.5, color: UI.ink, fontWeight: '600', paddingVertical: 6 },
  info: { fontSize: 12.5, color: '#2E7D32', fontWeight: '700', marginBottom: 6 },
  demande: { backgroundColor: UI.champ, borderRadius: 11, borderWidth: 1, borderColor: UI.accent,
             padding: 11, gap: 9, marginTop: 4, marginBottom: 6 },
  demandeTxt: { fontSize: 13, color: UI.ink, fontWeight: '700' },
  demandeLigne: { fontSize: 12, color: UI.muted },
  demandeBtns: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 14 },
  refuser: { fontSize: 12.5, color: UI.muted, fontWeight: '700' },
  accepter: { backgroundColor: UI.accent, borderRadius: 9, paddingHorizontal: 14, paddingVertical: 8 },
  accepterTxt: { color: '#fff', fontSize: 12.5, fontWeight: '800' },
  action: { paddingVertical: 11, borderTopWidth: 0.5, borderTopColor: UI.border },
  actionTxt: { fontSize: 14, color: UI.ink, fontWeight: '700' },
  actionDanger: { },
});
